// Clone an inquiry = one POST restaurant-inquiries with the items inline. The
// header is whatever the clone modal shows (restaurant may differ — an old
// inquiry doubles as a template); the items copy the REQUEST: what was asked,
// how much, how flexible. Quotes are never copied (prices go stale; the BE
// auto-quotes the new items from today's supplier prices), and neither is the
// expiry — it described the OLD stock.

import type {
  CreateRestaurantInquiryItem,
  CreateRestaurantInquiryPayload,
  PaymentOption,
  RestaurantInquiryItem,
} from 'apis/restaurant-inquiries';
import { isCreditOption } from 'pages/inquiry-processor/utils/payment';

export interface CloneHeader {
  restaurantId: number;
  paymentOption: PaymentOption;
  creditDays: number;
  notes: string;
}

const FLEX_FLAGS = ['brandFlexible', 'originFlexible', 'specFlexible', 'stateFlexible', 'packagingFlexible'] as const;

export function cloneItemPayload(item: RestaurantInquiryItem): CreateRestaurantInquiryItem {
  const out: CreateRestaurantInquiryItem = { masterProductId: item.masterProductId };
  // Absent = not captured; the BE 400s on null and defaults PER_ORDER / AED /
  // flexible=true, so only defined values travel.
  if (item.volume != null && item.volume > 0) out.volume = item.volume;
  if (item.volumePeriod) out.volumePeriod = item.volumePeriod;
  if (item.currentPrice != null) out.currentPrice = item.currentPrice;
  if (item.expectedPrice != null) out.expectedPrice = item.expectedPrice;
  if (item.currency) out.currency = item.currency;
  FLEX_FLAGS.forEach((flag) => {
    if (item[flag] != null) out[flag] = item[flag];
  });
  if (item.acceptableAlternatives) out.acceptableAlternatives = item.acceptableAlternatives;
  const notes = item.notes?.trim();
  if (notes) out.notes = notes;
  return out;
}

export function buildClonePayload(header: CloneHeader, items: RestaurantInquiryItem[]): CreateRestaurantInquiryPayload {
  const payload: CreateRestaurantInquiryPayload = {
    restaurantId: header.restaurantId,
    paymentOption: header.paymentOption,
    items: items.map(cloneItemPayload),
  };
  if (isCreditOption(header.paymentOption)) payload.creditDays = header.creditDays;
  const notes = header.notes.trim();
  if (notes) payload.notes = notes;
  // status omitted on purpose: defaults to NEW server-side.
  return payload;
}
